"use client";

import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Fade,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  ContentCopy,
  Delete,
  ErrorOutline,
  Key,
  VerifiedUser,
} from "@mui/icons-material";

type JsonValue =
  | Record<string, unknown>
  | unknown[]
  | string
  | number
  | boolean;

type JwtParseKind = "JWS" | "JWE";

type JwtParseResult = {
  kind: JwtParseKind;
  raw: string;
  parts: string[];
  signingInput: string | null;
  signature: string | null;
  headerText: string;
  payloadText: string | null;
  header: JsonValue | null;
  payload: JsonValue | null;
};

type VerificationState =
  | { status: "idle" }
  | { status: "unsupported"; reason: string }
  | { status: "missing"; reason: string }
  | { status: "verified" }
  | { status: "invalid" }
  | { status: "error"; reason: string };

function normalizeJwtInput(raw: string): string {
  const trimmed = raw.trim();
  const withoutBearer = trimmed.replace(/^bearer\s+/i, "");
  return withoutBearer.replace(/\s+/g, "");
}

function base64UrlToUint8Array(input: string): Uint8Array {
  const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
  const paddingNeeded = (4 - (normalized.length % 4)) % 4;
  const padded = normalized + "=".repeat(paddingNeeded);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function decodeBase64UrlToString(input: string): string {
  const bytes = base64UrlToUint8Array(input);
  return new TextDecoder().decode(bytes);
}

function tryParseJson(text: string): JsonValue | null {
  try {
    return JSON.parse(text) as JsonValue;
  } catch {
    return null;
  }
}

function stringifyJson(value: JsonValue | null): string {
  if (value === null) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function asNumericDate(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function formatRelativeSeconds(targetSeconds: number): string {
  const deltaSeconds = targetSeconds - Math.floor(Date.now() / 1000);
  const abs = Math.abs(deltaSeconds);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

  if (abs < 60) return rtf.format(deltaSeconds, "second");
  const deltaMinutes = Math.round(deltaSeconds / 60);
  if (Math.abs(deltaMinutes) < 60) return rtf.format(deltaMinutes, "minute");
  const deltaHours = Math.round(deltaSeconds / (60 * 60));
  if (Math.abs(deltaHours) < 24) return rtf.format(deltaHours, "hour");
  const deltaDays = Math.round(deltaSeconds / (60 * 60 * 24));
  if (Math.abs(deltaDays) < 30) return rtf.format(deltaDays, "day");
  const deltaMonths = Math.round(deltaSeconds / (60 * 60 * 24 * 30));
  if (Math.abs(deltaMonths) < 12) return rtf.format(deltaMonths, "month");
  const deltaYears = Math.round(deltaSeconds / (60 * 60 * 24 * 365));
  return rtf.format(deltaYears, "year");
}

function formatUnixSeconds(seconds: number): string {
  const date = new Date(seconds * 1000);
  const local = date.toLocaleString();
  return `${seconds} (${local}, ${formatRelativeSeconds(seconds)})`;
}

function pick(obj: JsonValue | null, key: string): unknown {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return undefined;
  return (obj as Record<string, unknown>)[key];
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function parseJwt(token: string): JwtParseResult {
  const normalized = normalizeJwtInput(token);
  let parts = normalized.split(".");
  while (parts.length > 3 && parts[parts.length - 1] === "") {
    parts = parts.slice(0, -1);
  }

  if (parts.length < 2) {
    throw new Error("JWT must have at least 2 parts (header.payload).");
  }

  if (parts.length === 5) {
    const headerText = decodeBase64UrlToString(parts[0]);
    const header = tryParseJson(headerText);
    return {
      kind: "JWE",
      raw: normalized,
      parts,
      signingInput: null,
      signature: null,
      headerText,
      payloadText: null,
      header,
      payload: null,
    };
  }

  if (parts.length !== 2 && parts.length !== 3) {
    throw new Error(
      `Unsupported JWT format. Expected 2 or 3 parts for JWS, or 5 parts for JWE. Got ${parts.length}.`
    );
  }

  const headerText = decodeBase64UrlToString(parts[0]);
  const payloadText = decodeBase64UrlToString(parts[1]);
  const header = tryParseJson(headerText);
  const payload = tryParseJson(payloadText);

  const signature = parts.length === 3 ? parts[2] : "";
  const signingInput = `${parts[0]}.${parts[1]}`;

  return {
    kind: "JWS",
    raw: normalized,
    parts,
    signingInput,
    signature,
    headerText,
    payloadText,
    header,
    payload,
  };
}

function claimRows(
  payload: JsonValue | null
): Array<{ label: string; value: string }> {
  const rows: Array<{ label: string; value: string }> = [];
  if (!payload || typeof payload !== "object" || Array.isArray(payload))
    return rows;
  const p = payload as Record<string, unknown>;

  const iss = p.iss;
  if (typeof iss === "string" && iss)
    rows.push({ label: "Issuer (iss)", value: iss });

  const sub = p.sub;
  if (typeof sub === "string" && sub)
    rows.push({ label: "Subject (sub)", value: sub });

  const aud = p.aud;
  if (typeof aud === "string" && aud)
    rows.push({ label: "Audience (aud)", value: aud });
  if (Array.isArray(aud) && aud.length) {
    rows.push({
      label: "Audience (aud)",
      value: aud.map((v) => String(v)).join(", "),
    });
  }

  const jti = p.jti;
  if (typeof jti === "string" && jti)
    rows.push({ label: "JWT ID (jti)", value: jti });

  const exp = asNumericDate(p.exp);
  if (exp !== null)
    rows.push({ label: "Expires (exp)", value: formatUnixSeconds(exp) });

  const nbf = asNumericDate(p.nbf);
  if (nbf !== null)
    rows.push({ label: "Not Before (nbf)", value: formatUnixSeconds(nbf) });

  const iat = asNumericDate(p.iat);
  if (iat !== null)
    rows.push({ label: "Issued At (iat)", value: formatUnixSeconds(iat) });

  return rows;
}

function tokenTimingChip(payload: JsonValue | null): {
  label: string;
  color: "success" | "warning" | "error" | "default";
} {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return { label: "No payload", color: "default" };
  }
  const now = Math.floor(Date.now() / 1000);
  const p = payload as Record<string, unknown>;
  const exp = asNumericDate(p.exp);
  const nbf = asNumericDate(p.nbf);

  if (exp !== null && now >= exp) return { label: "Expired", color: "error" };
  if (nbf !== null && now < nbf)
    return { label: "Not active yet", color: "warning" };
  if (exp !== null) return { label: "Active", color: "success" };
  return { label: "No exp claim", color: "default" };
}

export default function JwtParser() {
  const [input, setInput] = useState("");
  const [secret, setSecret] = useState("");
  const [parsed, setParsed] = useState<JwtParseResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedMessage, setCopiedMessage] = useState<string | null>(null);
  const [verification, setVerification] = useState<VerificationState>({
    status: "idle",
  });

  const syncTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (syncTimeout.current) clearTimeout(syncTimeout.current);

    syncTimeout.current = setTimeout(() => {
      const text = input.trim();
      if (!text) {
        setParsed(null);
        setVerification({ status: "idle" });
        return;
      }

      try {
        const next = parseJwt(text);
        setParsed(next);
      } catch {
        // Keep the UI quiet while typing; users can press Parse for errors.
      }
    }, 250);

    return () => {
      if (syncTimeout.current) clearTimeout(syncTimeout.current);
    };
  }, [input]);

  const handleParse = () => {
    setError(null);
    setVerification({ status: "idle" });

    const text = input.trim();
    if (!text) {
      setError("Please paste a JWT to parse.");
      setParsed(null);
      return;
    }

    try {
      const next = parseJwt(text);
      setParsed(next);
    } catch (e) {
      setParsed(null);
      const message = e instanceof Error ? e.message : "Invalid JWT.";
      setError(message);
    }
  };

  const handleClear = () => {
    setInput("");
    setSecret("");
    setParsed(null);
    setError(null);
    setVerification({ status: "idle" });
    setCopiedMessage(null);
  };

  const copyText = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessage(message);
      setTimeout(() => setCopiedMessage(null), 2000);
    } catch {
      setError("Failed to copy to clipboard.");
    }
  };

  const handleVerify = async () => {
    if (!parsed) {
      setVerification({ status: "missing", reason: "Parse a token first." });
      return;
    }

    if (parsed.kind !== "JWS") {
      setVerification({
        status: "unsupported",
        reason: "Encrypted (JWE) tokens cannot be verified or decoded here.",
      });
      return;
    }

    const headerAlg = pick(parsed.header, "alg");
    const alg = typeof headerAlg === "string" ? headerAlg : "";
    if (!alg) {
      setVerification({
        status: "unsupported",
        reason: "Missing alg in header.",
      });
      return;
    }

    if (alg === "none") {
      setVerification({
        status: "unsupported",
        reason: "alg=none tokens are unsigned (no signature to verify).",
      });
      return;
    }

    const hash =
      alg === "HS256"
        ? "SHA-256"
        : alg === "HS384"
          ? "SHA-384"
          : alg === "HS512"
            ? "SHA-512"
            : null;
    if (!hash) {
      setVerification({
        status: "unsupported",
        reason: `Signature verification supports HS256/HS384/HS512 only (got ${alg}).`,
      });
      return;
    }

    if (!secret.trim()) {
      setVerification({
        status: "missing",
        reason: "Enter a secret to verify HS signatures.",
      });
      return;
    }

    if (!parsed.signingInput) {
      setVerification({ status: "error", reason: "Missing signing input." });
      return;
    }

    if (parsed.signature === null) {
      setVerification({ status: "missing", reason: "Missing signature part." });
      return;
    }

    if (typeof crypto === "undefined" || !crypto.subtle) {
      setVerification({
        status: "unsupported",
        reason: "WebCrypto is not available in this browser context.",
      });
      return;
    }

    try {
      const key = await crypto.subtle.importKey(
        "raw",
        new TextEncoder().encode(secret),
        { name: "HMAC", hash: { name: hash } },
        false,
        ["sign"]
      );
      const sig = await crypto.subtle.sign(
        "HMAC",
        key,
        new TextEncoder().encode(parsed.signingInput)
      );
      const computed = bufferToBase64Url(sig);
      const provided = parsed.signature.replace(/=+$/g, "");
      setVerification(
        computed === provided ? { status: "verified" } : { status: "invalid" }
      );
    } catch (e) {
      const message =
        e instanceof Error ? e.message : "Failed to verify signature.";
      setVerification({ status: "error", reason: message });
    }
  };

  const headerJson = parsed?.header ? stringifyJson(parsed.header) : "";
  const payloadJson = parsed?.payload ? stringifyJson(parsed.payload) : "";
  const timing = parsed?.payload ? tokenTimingChip(parsed.payload) : null;
  const claims = parsed?.payload ? claimRows(parsed.payload) : [];

  const headerAlg = parsed?.header ? pick(parsed.header, "alg") : undefined;
  const headerTyp = parsed?.header ? pick(parsed.header, "typ") : undefined;
  const headerKid = parsed?.header ? pick(parsed.header, "kid") : undefined;

  return (
    <Box sx={{ maxWidth: 980, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Input */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          JWT
        </Typography>
        <TextField
          multiline
          rows={5}
          fullWidth
          placeholder="Paste your JWT here (header.payload.signature)"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
            setVerification({ status: "idle" });
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: "monospace",
              fontSize: "0.9rem",
            },
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Tip: If your token starts with{" "}
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            Bearer{" "}
          </Box>
          we&apos;ll ignore it. Decoding runs locally in your browser.
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Secret (optional, for HS256/384/512 verification)
          </Typography>
          <TextField
            fullWidth
            type="password"
            placeholder="Enter HMAC secret to verify the signature (never sent anywhere)"
            value={secret}
            onChange={(e) => {
              setSecret(e.target.value);
              setError(null);
              setVerification({ status: "idle" });
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: "monospace",
                fontSize: "0.9rem",
              },
            }}
          />
        </Box>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        {/* Actions */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 3 }}
          flexWrap="wrap"
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<Key sx={{ color: "#ffffff" }} />}
            onClick={handleParse}
            disabled={!input.trim()}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: "1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            Parse JWT
          </Button>

          <Button
            variant="outlined"
            size="large"
            startIcon={<VerifiedUser />}
            onClick={handleVerify}
            disabled={!parsed || parsed.kind !== "JWS"}
            sx={{ py: 1.5, px: 3, fontWeight: 600 }}
          >
            Verify HS Signature
          </Button>

          <Button
            variant="outlined"
            size="large"
            startIcon={<Delete />}
            onClick={handleClear}
            sx={{ py: 1.5, px: 3, fontWeight: 600 }}
          >
            Clear
          </Button>
        </Stack>

        {/* Copied notifications */}
        <Fade in={Boolean(copiedMessage)}>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <CheckCircle color="success" />
            <Typography color="success.main" fontWeight={600}>
              {copiedMessage || ""}
            </Typography>
          </Box>
        </Fade>

        {/* Verification status */}
        {verification.status !== "idle" && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {verification.status === "verified" ? (
              <>
                <CheckCircle color="success" />
                <Typography color="success.main" fontWeight={700}>
                  Signature verified (HS)
                </Typography>
              </>
            ) : verification.status === "invalid" ? (
              <>
                <ErrorOutline color="error" />
                <Typography color="error" fontWeight={700}>
                  Invalid signature (HS)
                </Typography>
              </>
            ) : (
              <>
                <ErrorOutline color="warning" />
                <Typography color="text.secondary" fontWeight={700}>
                  {verification.status === "missing"
                    ? verification.reason
                    : verification.status === "unsupported"
                      ? verification.reason
                      : verification.reason}
                </Typography>
              </>
            )}
          </Box>
        )}

        {parsed && (
          <Box sx={{ mt: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2.25,
                borderRadius: 1,
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.14), rgba(118, 75, 162, 0.12))",
                border: "1px solid rgba(102, 126, 234, 0.18)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Decoded Token
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    JWTs are Base64URL encoded, not encrypted. Treat payloads as
                    readable data.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={parsed.kind}
                    variant="outlined"
                    sx={{ fontWeight: 700, bgcolor: "rgba(255,255,255,0.7)" }}
                  />
                  {typeof headerAlg === "string" && headerAlg && (
                    <Chip
                      label={`alg: ${headerAlg}`}
                      variant="outlined"
                      sx={{ fontWeight: 700, bgcolor: "rgba(255,255,255,0.7)" }}
                    />
                  )}
                  {typeof headerTyp === "string" && headerTyp && (
                    <Chip
                      label={`typ: ${headerTyp}`}
                      variant="outlined"
                      sx={{ fontWeight: 700, bgcolor: "rgba(255,255,255,0.7)" }}
                    />
                  )}
                  {timing && (
                    <Chip
                      label={timing.label}
                      color={timing.color}
                      sx={{ fontWeight: 800 }}
                    />
                  )}
                </Stack>
              </Box>

              {typeof headerKid === "string" && headerKid && (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    mb: 2,
                  }}
                >
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    kid:
                  </Box>{" "}
                  {headerKid}
                </Typography>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      height: "100%",
                      background: "rgba(255,255,255,0.9)",
                      border: "1px solid rgba(102, 126, 234, 0.2)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        Header
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContentCopy />}
                        onClick={() =>
                          copyText(
                            parsed.header ? headerJson : parsed.headerText,
                            "Copied header JSON"
                          )
                        }
                        disabled={!parsed.headerText}
                      >
                        Copy
                      </Button>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={8}
                      value={parsed.header ? headerJson : parsed.headerText}
                      InputProps={{ readOnly: true }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontFamily: "monospace",
                          fontSize: "0.85rem",
                        },
                      }}
                    />
                    {!parsed.header && (
                      <Typography
                        variant="body2"
                        color="warning.main"
                        sx={{ mt: 1 }}
                      >
                        Header is not valid JSON.
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      height: "100%",
                      background: "rgba(255,255,255,0.9)",
                      border: "1px solid rgba(102, 126, 234, 0.2)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        Payload
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ContentCopy />}
                          onClick={() =>
                            copyText(
                              payloadJson || parsed.payloadText || "",
                              "Copied payload JSON"
                            )
                          }
                          disabled={!parsed.payloadText}
                        >
                          Copy
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ContentCopy />}
                          onClick={() =>
                            copyText(
                              JSON.stringify(
                                {
                                  header: parsed.header ?? parsed.headerText,
                                  payload: parsed.payload ?? parsed.payloadText,
                                  signature: parsed.signature ?? undefined,
                                },
                                null,
                                2
                              ),
                              "Copied decoded object"
                            )
                          }
                          disabled={!parsed.payloadText}
                        >
                          Copy All
                        </Button>
                      </Stack>
                    </Box>

                    {parsed.kind === "JWE" ? (
                      <Typography variant="body2" color="text.secondary">
                        This looks like an encrypted JWT (JWE). We can show the
                        header, but the payload is encrypted.
                      </Typography>
                    ) : (
                      <>
                        <TextField
                          fullWidth
                          multiline
                          minRows={8}
                          value={
                            parsed.payload
                              ? payloadJson
                              : parsed.payloadText || ""
                          }
                          InputProps={{ readOnly: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                            },
                          }}
                        />
                        {!parsed.payload && (
                          <Typography
                            variant="body2"
                            color="warning.main"
                            sx={{ mt: 1 }}
                          >
                            Payload is not valid JSON.
                          </Typography>
                        )}
                      </>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              {parsed.kind === "JWS" && (
                <>
                  <Divider
                    sx={{
                      my: 2,
                      borderColor: "transparent",
                      height: 2,
                      background:
                        "linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.45), transparent)",
                    }}
                  />

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          background: "rgba(255,255,255,0.75)",
                          border: "1px dashed rgba(102, 126, 234, 0.2)",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ mb: 1 }}
                        >
                          Standard Claims
                        </Typography>
                        {claims.length ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            {claims.map((row) => (
                              <Box
                                key={row.label}
                                sx={{
                                  p: 1.25,
                                  borderRadius: 0.75,
                                  background: "rgba(255,255,255,0.9)",
                                  border: "1px solid rgba(102, 126, 234, 0.18)",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textTransform: "uppercase",
                                    letterSpacing: 0.6,
                                  }}
                                  color="text.secondary"
                                >
                                  {row.label}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mt: 0.5,
                                    fontFamily: "monospace",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {row.value}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No standard registered claims found (iss, sub, aud,
                            exp, nbf, iat, jti).
                          </Typography>
                        )}
                      </Paper>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          background: "rgba(255,255,255,0.75)",
                          border: "1px dashed rgba(102, 126, 234, 0.2)",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ mb: 1 }}
                        >
                          Signature
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Decoding does not prove authenticity. Use verification
                          for HS tokens, or validate on your server.
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          value={parsed.signature ?? ""}
                          InputProps={{ readOnly: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              fontFamily: "monospace",
                              fontSize: "0.85rem",
                            },
                          }}
                        />
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 1 }}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentCopy />}
                            onClick={() =>
                              copyText(
                                parsed.signature ?? "",
                                "Copied signature"
                              )
                            }
                            disabled={!parsed.signature}
                          >
                            Copy
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentCopy />}
                            onClick={() => copyText(parsed.raw, "Copied token")}
                            disabled={!parsed.raw}
                          >
                            Copy Token
                          </Button>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>
                </>
              )}
            </Paper>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
